import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Trash2, FileText, ArrowRight, Building2, User, Minus, Plus, Upload, Ruler, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

const AREAS = ["Kampala CBD", "Ntinda", "Kololo", "Nakawa", "Rubaga", "Makindye", "Entebbe Road", "Other / Upcountry"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function QuoteCheckout() {
  const { items, setQty, setDims, remove, clear } = useCart();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", buyer_type: "individual",
    business_name: "", delivery_area: AREAS[0], notes: "",
  });
  const [artwork, setArtwork] = useState(null); // {path, filename, preview}
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [ref, setRef] = useState(null);
  const [doneItems, setDoneItems] = useState([]);
  const [doneEmailed, setDoneEmailed] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError("Artwork file too large — 10MB max.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setArtwork({
        path: data.path,
        filename: data.filename,
        preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please tell us your name and phone number so we can call back.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || null,
        business_name: form.buyer_type === "business" ? form.business_name.trim() || null : null,
        notes: form.notes.trim() || null,
        artwork: artwork?.path || null,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, qty: i.qty, spec: i.spec || "" })),
      };
      const { data } = await api.post("/quotes", payload);
      setDoneItems(items);
      setDoneEmailed(Boolean(form.email.trim()));
      clear();
      setRef(data.rfq_ref);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  /* ---------- success ---------- */
  if (ref) {
    return (
      <main className="pt-40 pb-24 min-h-screen flex items-start justify-center" data-testid="quote-success-page">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-lg w-full mx-5 rounded-3xl border border-mint/25 bg-moss/40 p-10 text-center"
        >
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 220, damping: 14 }}>
            <CheckCircle2 size={54} className="mx-auto text-mint" />
          </motion.div>
          <h1 className="font-display font-semibold text-3xl text-kraft mt-6">Quote request in</h1>
          <p className="font-mono text-2xl tracking-[0.2em] text-mint mt-4" data-testid="rfq-ref">{ref}</p>
          <p className="text-sm text-kraft/65 mt-5 leading-relaxed">
            We received your list{artwork ? " and your artwork" : ""}. Our team will call you within one business
            day with a price per box — keep this reference handy.
            {doneEmailed && " A copy was also emailed to you."}
          </p>
          <p className="font-mono text-[11px] tracking-[0.15em] text-mintsoft/90 mt-4" data-testid="success-call-us">
            In a hurry? Call us — <a href="tel:+256750928239" className="text-mint hover:text-kraft transition-colors">0750 928 239</a> or <a href="tel:+256781928239" className="text-mint hover:text-kraft transition-colors">0781 928 239</a>
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a
              href={`https://wa.me/256781928239?text=${encodeURIComponent(
                `Hi MUBFAR500! I just sent quote request ${ref}: ${doneItems.map((i) => `${i.qty}× ${i.name}`).join(", ")}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-whatsapp-btn"
              className="rounded-full bg-[#25D366] text-forest font-semibold px-6 h-11 inline-flex items-center gap-2 hover:bg-kraft transition-colors"
            >
              <MessageCircle size={16} /> Send to WhatsApp
            </a>
            <button onClick={() => navigate("/shop")} data-testid="success-shop-btn" className="rounded-full bg-mint text-forest font-semibold px-6 h-11 hover:bg-kraft transition-colors">
              Keep browsing
            </button>
            <button onClick={() => navigate("/")} data-testid="success-home-btn" className="rounded-full border border-mint/40 text-kraft px-6 h-11 hover:border-mint hover:text-mint transition-colors">
              Back home
            </button>
          </div>
        </motion.div>
      </main>
    );
  }

  /* ---------- empty cart ---------- */
  if (items.length === 0) {
    return (
      <main className="pt-40 pb-24 min-h-screen" data-testid="quote-empty-page">
        <div className="max-w-md mx-auto px-5 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl border-2 border-dashed border-mint/30 grid place-items-center mb-5">
            <FileText size={24} className="text-mintsoft/60" />
          </div>
          <h1 className="font-display font-semibold text-3xl text-kraft mb-3">Nothing to quote yet</h1>
          <p className="text-sm text-kraft/60 mb-7">
            Add boxes from the shop first — then tell us sizes and volume, and we quote fast.
          </p>
          <button onClick={() => navigate("/shop")} data-testid="quote-empty-shop-btn" className="rounded-full bg-mint text-forest font-semibold px-7 h-12 hover:bg-kraft transition-colors">
            Browse the shop
          </button>
        </div>
      </main>
    );
  }

  /* ---------- checkout form ---------- */
  return (
    <main className="pt-32 pb-24 min-h-screen" data-testid="quote-checkout-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">Checkout — Quote Request</p>
        <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-kraft mb-3">
          Tell us the box. We tell you the price.
        </h1>
        <p className="text-kraft/60 mb-12 max-w-xl">
          No payment now. Send your sizes, volume and artwork — we call back with a quote per box.
        </p>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* items */}
          <div className="lg:col-span-5 space-y-4" data-testid="checkout-items-list">
            {items.map((it) => (
              <div key={it.key} className="flex gap-4 rounded-2xl border border-mint/15 bg-moss/30 p-4" data-testid="checkout-item">
                <img src={it.image} alt={it.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-kraft leading-snug">{it.name}</p>
                  {it.spec && <p className="font-mono text-[10px] text-mintsoft/80 mt-1">{it.spec}</p>}

                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex items-center border border-mint/25 rounded-full">
                      <button onClick={() => setQty(it.key, it.qty - 1)} data-testid="checkout-qty-minus" className="px-3 h-8 grid place-items-center text-kraft hover:text-mint">
                        <Minus size={12} />
                      </button>
                      <span className="w-10 text-center font-mono text-xs text-kraft" data-testid="checkout-qty-value">{it.qty}</span>
                      <button onClick={() => setQty(it.key, it.qty + 1)} data-testid="checkout-qty-plus" className="px-3 h-8 grid place-items-center text-kraft hover:text-mint">
                        <Plus size={12} />
                      </button>
                    </div>
                    <button onClick={() => remove(it.key)} data-testid="checkout-item-remove" className="text-kraft/40 hover:text-red-400 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {/* own measurements */}
                  <div className="mt-3">
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-mintsoft/70 flex items-center gap-1.5 mb-1.5">
                      <Ruler size={10} /> Own size? L × W × H cm
                    </p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {["l", "w", "h"].map((dim) => (
                        <input
                          key={dim}
                          type="number"
                          min="0"
                          placeholder={dim.toUpperCase()}
                          value={(it.dims || {})[dim] ?? ""}
                          onChange={(e) =>
                            setDims(it.key, { ...(it.dims || {}), [dim]: e.target.value === "" ? "" : +e.target.value })
                          }
                          data-testid={`dims-${dim}-${it.key}`}
                          className="h-8 rounded-lg bg-forest/60 border border-mint/20 px-2 text-center font-mono text-xs text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint"
                        />
                      ))}
                    </div>
                    <p className="font-mono text-[9px] tracking-[0.12em] uppercase text-kraft/35 mt-1.5">
                      Leave blank for the standard size
                    </p>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => navigate("/shop")} data-testid="checkout-add-more" className="font-mono text-[10px] tracking-[0.2em] uppercase text-mint hover:text-kraft transition-colors">
              + Add more boxes
            </button>
          </div>

          {/* form */}
          <form onSubmit={submit} className="lg:col-span-7 rounded-3xl border border-mint/15 bg-moss/20 p-7 sm:p-9" data-testid="checkout-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Your name *</label>
                <input required value={form.name} onChange={set("name")} data-testid="checkout-name-input" placeholder="Amina Okello"
                  className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Phone *</label>
                <input required value={form.phone} onChange={set("phone")} data-testid="checkout-phone-input" placeholder="+256 7XX XXX XXX"
                  className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Email (optional)</label>
                <input type="email" value={form.email} onChange={set("email")} data-testid="checkout-email-input" placeholder="you@brand.ug"
                  className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Delivery area</label>
                <select value={form.delivery_area} onChange={set("delivery_area")} data-testid="checkout-area-select"
                  className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft focus:outline-none focus:border-mint">
                  {AREAS.map((a) => <option key={a} value={a} className="bg-forest">{a}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">I'm ordering as</label>
              <div className="flex gap-3">
                {[
                  ["individual", "Individual", User],
                  ["business", "A business", Building2],
                ].map(([val, label, Icon]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setForm((f) => ({ ...f, buyer_type: val }))}
                    data-testid={`buyer-type-${val}`}
                    className={`flex-1 h-12 rounded-xl border flex items-center justify-center gap-2 text-sm transition-colors ${
                      form.buyer_type === val
                        ? "border-mint bg-mint/10 text-mint font-semibold"
                        : "border-mint/25 text-kraft/60 hover:border-mint/60"
                    }`}
                  >
                    <Icon size={16} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {form.buyer_type === "business" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-5 overflow-hidden">
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Business name</label>
                <input value={form.business_name} onChange={set("business_name")} data-testid="checkout-business-input" placeholder="Bella Bakery Ltd"
                  className="w-full h-12 rounded-xl bg-forest/60 border border-mint/25 px-4 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint" />
              </motion.div>
            )}

            {/* artwork upload */}
            <div className="mt-6">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">
                Artwork / logo (optional)
              </label>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                className="hidden"
                onChange={onFile}
                data-testid="artwork-file-input"
              />
              {artwork ? (
                <div className="flex items-center gap-4 rounded-xl border border-mint/25 bg-forest/60 p-4" data-testid="artwork-preview">
                  {artwork.preview ? (
                    <img src={artwork.preview} alt="Artwork preview" className="w-14 h-14 rounded-lg object-cover border border-mint/20" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg border border-mint/20 grid place-items-center">
                      <FileText size={20} className="text-mintsoft/70" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-kraft truncate">{artwork.filename}</p>
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-mint">Uploaded — ships with your request</p>
                  </div>
                  <button type="button" onClick={() => setArtwork(null)} data-testid="artwork-remove-btn" className="text-kraft/40 hover:text-red-400 transition-colors p-2">
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="artwork-upload-btn"
                  className="w-full h-14 rounded-xl border-2 border-dashed border-mint/30 text-mintsoft/80 text-sm flex items-center justify-center gap-2 hover:border-mint hover:text-mint transition-colors disabled:opacity-60"
                >
                  <Upload size={16} />
                  {uploading ? "Uploading…" : "Attach artwork — JPG, PNG, WEBP or PDF, 10MB max"}
                </button>
              )}
            </div>

            <div className="mt-6">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">Notes (optional)</label>
              <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="checkout-notes-input"
                placeholder="Print colours, deadlines, sample requests…"
                className="w-full rounded-xl bg-forest/60 border border-mint/25 px-4 py-3 text-sm text-kraft placeholder:text-kraft/30 focus:outline-none focus:border-mint resize-none" />
            </div>

            {error && (
              <p className="mt-5 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-xl px-4 py-3" data-testid="checkout-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={sending || uploading}
              data-testid="checkout-submit-btn"
              className="mt-8 w-full rounded-full bg-mint text-forest font-semibold h-[52px] flex items-center justify-center gap-2 hover:bg-kraft transition-colors disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send quote request"}
              {!sending && <ArrowRight size={17} />}
            </button>
            <p className="mt-4 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-kraft/40">
              We reply within one business day
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}
